import { FC, useEffect, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faIdCard,
  faCalendarPlus,
  faPen,
  faCalendarTimes,
  faBullhorn,
  faTrash,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import {
  apiGetDashboardFeed,
  apiPostDashboardMessage,
  apiDeleteDashboardMessage,
} from '../../api/dashboardApi';
import type { DashboardMessage, DashboardMessageType } from '../../types/models/dashboard';
import type { RootState } from '../../types/store/state';

// ─── Config ───────────────────────────────────────────────────────────────────

const TYPE_CONFIG: Record<DashboardMessageType, { color: string; icon: any; label: string }> = {
  card_found:  { color: '#C9A44A', icon: faIdCard,        label: 'Carta trovata' },
  ppv_created: { color: '#7BAF7B', icon: faCalendarPlus,  label: 'Nuovo PPV' },
  ppv_updated: { color: '#6B9EC7', icon: faPen,           label: 'PPV aggiornato' },
  ppv_deleted: { color: '#B05050', icon: faCalendarTimes, label: 'PPV rimosso' },
  manual:      { color: 'rgb(163, 27, 27)', icon: faBullhorn, label: 'Annuncio' },
};

function timeAgo(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'adesso';
  if (mins < 60) return `${mins}m fa`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h fa`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'ieri';
  return `${days}g fa`;
}

// ─── Styled ───────────────────────────────────────────────────────────────────

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.25; }
`;

const Wrapper = styled.div<{ $isMobile?: boolean }>`
  width: ${({ $isMobile }) => ($isMobile ? '100%' : '90%')};
  margin: ${({ $isMobile }) => ($isMobile ? '0 0 10px' : '0 0 20px')};
  background-color: rgb(31, 31, 31);
  border: 1px solid rgba(163, 27, 27, 0.3);
  border-radius: 10px;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 18px;
  border-bottom: 1px solid rgba(163, 27, 27, 0.2);
  background-color: rgba(163, 27, 27, 0.08);
`;

const LiveDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background-color: rgb(163, 27, 27);
  flex-shrink: 0;
  animation: ${pulse} 2.2s ease-in-out infinite;
`;

const HeaderTitle = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.13em;
  flex: 1;
`;

const AdminToggleBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: 1px solid rgba(163, 27, 27, 0.4);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 11px;
  font-weight: 600;
  padding: 4px 10px;
  cursor: pointer;
  transition: all 0.15s;
  letter-spacing: 0.04em;

  &:hover {
    border-color: rgb(163, 27, 27);
    color: rgba(255, 255, 255, 0.85);
    background-color: rgba(163, 27, 27, 0.12);
  }
`;

const AdminPanel = styled.div`
  padding: 14px 18px;
  border-bottom: 1px solid rgba(163, 27, 27, 0.15);
  background-color: rgba(163, 27, 27, 0.04);
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AdminTextarea = styled.textarea`
  width: 100%;
  height: 72px;
  background-color: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 7px;
  color: #fff;
  font-size: 13px;
  padding: 10px 12px;
  resize: none;
  font-family: inherit;
  box-sizing: border-box;
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: rgba(163, 27, 27, 0.55);
  }
  &::placeholder { color: rgba(255, 255, 255, 0.22); }
`;

const AdminRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const CharCount = styled.span<{ $warn: boolean }>`
  font-size: 11px;
  color: ${({ $warn }) => ($warn ? '#B05050' : 'rgba(255,255,255,0.25)')};
`;

const SendBtn = styled.button`
  padding: 7px 18px;
  background-color: rgb(163, 27, 27);
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  letter-spacing: 0.04em;
  transition: background-color 0.15s;

  &:hover:not(:disabled) { background-color: rgb(190, 35, 35); }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const FeedList = styled.div<{ $isMobile?: boolean }>`
  max-height: ${({ $isMobile }) => ($isMobile ? '150px' : '190px')};
  overflow-y: auto;

  &::-webkit-scrollbar { width: 3px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
`;

const FeedItem = styled.div<{ $color: string }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 11px 18px;
  border-left: 3px solid ${({ $color }) => $color};
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  transition: background-color 0.12s;

  &:last-child { border-bottom: none; }
  &:hover { background-color: rgba(163, 27, 27, 0.05); }
`;

const TypeIcon = styled.div<{ $color: string }>`
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background-color: ${({ $color }) => $color}1a;
  color: ${({ $color }) => $color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  flex-shrink: 0;
  margin-top: 2px;
`;

const ItemContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemBody = styled.p<{ $isMobile?: boolean }>`
  margin: 0 0 4px;
  font-size: ${({ $isMobile }) => ($isMobile ? '12px' : '13px')};
  color: rgba(255, 255, 255, 0.82);
  line-height: 1.5;
`;

const ItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TypeBadge = styled.span<{ $color: string }>`
  font-size: 10px;
  font-weight: 700;
  color: ${({ $color }) => $color};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Dot = styled.span`
  color: rgba(255, 255, 255, 0.15);
  font-size: 10px;
`;

const TimeStamp = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.22);
`;

const Author = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.3);
  font-style: italic;
`;

const DeleteBtn = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.15);
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  flex-shrink: 0;
  transition: color 0.12s;
  margin-left: 4px;

  &:hover { color: #B05050; }
`;

const EmptyState = styled.p`
  padding: 32px 18px;
  text-align: center;
  color: rgba(255, 255, 255, 0.2);
  font-size: 13px;
  margin: 0;
  font-style: italic;
`;

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  isMobile?: boolean;
}

const NewsFeed: FC<Props> = ({ isMobile }) => {
  const user = useSelector((s: RootState) => s.auth.user);
  const isAdmin = !!user && (user.is_admin || user.is_founder || user.is_super_admin);

  const [messages, setMessages] = useState<DashboardMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());

  const load = useCallback(async () => {
    try {
      const res = await apiGetDashboardFeed(20);
      const data: DashboardMessage[] = Array.isArray(res) ? res : (res as any)?.data ?? [];
      setMessages(data);
    } catch {
      // silenzioso
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    apiGetDashboardFeed(20)
      .then((res) => {
        if (!active) return;
        const data: DashboardMessage[] = Array.isArray(res) ? res : (res as any)?.data ?? [];
        setMessages(data);
      })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const handleSend = async () => {
    const body = input.trim();
    if (!body || body.length > 500 || sending) return;
    setSending(true);
    try {
      await apiPostDashboardMessage(body);
      setInput('');
      await load();
    } catch {
      // silenzioso
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingIds((prev) => new Set(prev).add(id));
    try {
      await apiDeleteDashboardMessage(id);
      setMessages((prev) => prev.filter((m) => m.id !== id));
    } catch {
      // silenzioso
    } finally {
      setDeletingIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  return (
    <Wrapper $isMobile={isMobile}>
      <Header>
        <LiveDot />
        <HeaderTitle>GWF Live</HeaderTitle>
        {isAdmin && (
          <AdminToggleBtn onClick={() => setPanelOpen((v) => !v)}>
            <FontAwesomeIcon icon={faBullhorn} />
            Pubblica
            <FontAwesomeIcon icon={panelOpen ? faChevronUp : faChevronDown} />
          </AdminToggleBtn>
        )}
      </Header>

      {isAdmin && panelOpen && (
        <AdminPanel>
          <AdminTextarea
            placeholder="Scrivi un annuncio per la community..."
            value={input}
            onChange={(e) => setInput(e.target.value.slice(0, 500))}
          />
          <AdminRow>
            <CharCount $warn={input.length > 450}>{input.length} / 500</CharCount>
            <SendBtn
              onClick={handleSend}
              disabled={!input.trim() || input.length > 500 || sending}
            >
              <FontAwesomeIcon icon={faBullhorn} />
              {sending ? 'Invio...' : 'Pubblica'}
            </SendBtn>
          </AdminRow>
        </AdminPanel>
      )}

      <FeedList $isMobile={isMobile}>
        {loading ? (
          <EmptyState>Caricamento...</EmptyState>
        ) : messages.length === 0 ? (
          <EmptyState>Nessuna attività recente.</EmptyState>
        ) : (
          messages.map((msg) => {
            const cfg = TYPE_CONFIG[msg.type] ?? TYPE_CONFIG.manual;
            return (
              <FeedItem key={msg.id} $color={cfg.color}>
                <TypeIcon $color={cfg.color}>
                  <FontAwesomeIcon icon={cfg.icon} />
                </TypeIcon>
                <ItemContent>
                  <ItemBody $isMobile={isMobile}>{msg.body}</ItemBody>
                  <ItemMeta>
                    <TypeBadge $color={cfg.color}>{cfg.label}</TypeBadge>
                    <Dot>·</Dot>
                    <Author>
                      {msg.type === 'manual' ? (msg.username ?? 'Admin') : 'Sistema'}
                    </Author>
                    <Dot>·</Dot>
                    <TimeStamp>{timeAgo(msg.created_at)}</TimeStamp>
                  </ItemMeta>
                </ItemContent>
                {isAdmin && (
                  <DeleteBtn
                    onClick={() => handleDelete(msg.id)}
                    disabled={deletingIds.has(msg.id)}
                    title="Elimina"
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </DeleteBtn>
                )}
              </FeedItem>
            );
          })
        )}
      </FeedList>
    </Wrapper>
  );
};

export default NewsFeed;
